import {
  Building,
  Button,
  getRegionOptions,
  Globe,
  Input,
  Mail,
  MapPin,
  PhoneInput,
  Specialty,
  SpecialtySearchAutocomplete,
} from "@ews-admin/global-design-system";
import { Select } from "../../atoms/Select";
import { facilityTypeLabel } from "../../../utils/enum-label-utils";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  FacilityFormData,
  facilityFormSchema,
} from "../../../schemas/onboarding.schema";
import { facilitiesApi } from "../../../services/facilities.api";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { searchFacilitiesRequest } from "../../../store/reducers/facilitySlice";
import {
  fetchSpecialtiesRequest,
  selectSpecialties,
} from "../../../store/reducers/specialtySlice";
import { FacilityType } from "../../../types/facility.definition";
import { AcceptedCountries } from "../../../types/user.definition";
import { Modal } from "../Modal/Modal";

interface AddFacilityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddFacilityModal: React.FC<AddFacilityModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const storeSpecialties = useAppSelector(selectSpecialties);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState<Specialty[]>(
    []
  );
  const stateOptions = getRegionOptions(AcceptedCountries.SENEGAL);

  const specialtiesForWidget: Specialty[] = storeSpecialties.map(dto => ({
    id: String(dto.id),
    code: dto.code,
    label: dto.label ?? "",
  }));

  // MED-1464: one option per FacilityType, labelled from the catalogue, so a
  // type added to the enum can no longer be silently missing from the list.
  const facilityTypeOptions = Object.values(FacilityType).map(type => ({
    label: facilityTypeLabel(t, type),
    value: type,
  }));

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FacilityFormData>({
    resolver: zodResolver(facilityFormSchema(t)),
    defaultValues: {
      name: "",
      type: "" as FacilityType,
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      accessInformation: "",
      photoUrl: "",
      website: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchSpecialtiesRequest({ lang: i18n.language }));
    } else {
      reset();
      setSelectedSpecialties([]);
    }
  }, [isOpen, reset, dispatch, i18n.language]);

  const websiteReg = register("website");

  const onSubmit = async (data: FacilityFormData) => {
    const website = data.website;
    const normalizedWebsite =
      website &&
      !website.startsWith("http://") &&
      !website.startsWith("https://")
        ? `https://${website}`
        : website || null;

    setIsSubmitting(true);
    try {
      await facilitiesApi.createFacility({
        name: data.name,
        facilityType: data.type,
        contactEmail: data.email,
        phoneNumber: data.phone,
        website: normalizedWebsite,
        location: {
          address: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
          accessInformation: data.accessInformation || undefined,
          country: AcceptedCountries.SENEGAL,
        },
        hours: [
          {
            monday: { open: "08:00", close: "18:00" },
            tuesday: { open: "08:00", close: "18:00" },
            wednesday: { open: "08:00", close: "18:00" },
            thursday: { open: "08:00", close: "18:00" },
            friday: { open: "08:00", close: "18:00" },
            saturday: { open: "09:00", close: "17:00" },
            sunday: { open: "10:00", close: "16:00" },
          },
        ],
        careTypeIds: [],
        specialtyIds: selectedSpecialties.map(s => Number(s.id)),
        insurance: [],
        isOpen: true,
        isPartner: false,
        healthcareFacilityServices: [],
        photoUrl: data.photoUrl || "",
        rating: 0,
        reviews: 0,
      });
      toast.success(t("admin.facilities.addModal.success"));
      dispatch(
        searchFacilitiesRequest({
          keyword: "",
          offset: 0,
          limit: 10,
          sortBy: "name",
          sortOrder: "asc",
        })
      );
      onClose();
    } catch {
      toast.error(t("admin.facilities.addModal.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !isSubmitting && onClose()}
      title={t("admin.facilities.addModal.title")}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Input
              type="text"
              label={t("onboarding.facility.name")}
              required
              placeholder={t("onboarding.facility.namePlaceholder")}
              leftIcon={<Building className="w-4 h-4" />}
              error={errors.name?.message}
              {...register("name")}
            />

            <div>
              <Controller
                name="type"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Select
                      label={t("onboarding.facility.type")}
                      required
                      value={field.value}
                      onChange={field.onChange}
                      options={facilityTypeOptions}
                    />
                    {fieldState.error && (
                      <p className="mt-1 text-sm text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>

            <Input
              type="email"
              label={t("onboarding.facility.email")}
              required
              placeholder={t("onboarding.facility.emailPlaceholder")}
              leftIcon={<Mail className="w-4 h-4" />}
              error={errors.email?.message}
              {...register("email")}
            />

            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState }) => (
                <PhoneInput
                  label={t("onboarding.facility.phone")}
                  required
                  {...field}
                  error={fieldState.error?.message}
                />
              )}
            />

            <div className="md:col-span-2">
              <Input
                type="text"
                label={t("onboarding.facility.address")}
                required
                placeholder={t("onboarding.facility.addressPlaceholder")}
                leftIcon={<MapPin className="w-4 h-4" />}
                error={errors.address?.message}
                {...register("address")}
              />
            </div>

            <Input
              type="text"
              label={t("onboarding.facility.city")}
              required
              placeholder={t("onboarding.facility.cityPlaceholder")}
              leftIcon={<MapPin className="w-4 h-4" />}
              error={errors.city?.message}
              {...register("city")}
            />

            <div>
              <Controller
                name="state"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Select
                      label={t("onboarding.facility.state")}
                      required
                      value={field.value}
                      onChange={field.onChange}
                      options={stateOptions}
                      placeholder={t("onboarding.facility.statePlaceholder")}
                    />
                    {fieldState.error && (
                      <p className="mt-1 text-sm text-red-600">
                        {fieldState.error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>

            <Input
              type="text"
              label={t("onboarding.facility.zip")}
              placeholder={t("onboarding.facility.zipPlaceholder")}
              leftIcon={<MapPin className="w-4 h-4" />}
              error={errors.zip?.message}
              {...register("zip")}
            />

            <div className="md:col-span-2">
              <Input
                type="text"
                label={t("onboarding.facility.accessInformation")}
                placeholder={t(
                  "onboarding.facility.accessInformationPlaceholder"
                )}
                {...register("accessInformation")}
              />
            </div>

            <Input
              type="url"
              label={t("onboarding.facility.website")}
              placeholder={t("onboarding.facility.websitePlaceholder")}
              leftIcon={<Globe className="w-4 h-4" />}
              error={errors.website?.message}
              {...websiteReg}
              onBlur={e => {
                const val = e.target.value;
                if (
                  val &&
                  !val.startsWith("http://") &&
                  !val.startsWith("https://")
                ) {
                  setValue("website", `https://${val}`, {
                    shouldValidate: true,
                  });
                }
                websiteReg.onBlur(e);
              }}
            />

            <div className="md:col-span-2">
              <Input
                type="url"
                label={t("onboarding.facility.photoUrl")}
                placeholder={t("onboarding.facility.photoUrlPlaceholder")}
                error={errors.photoUrl?.message}
                {...register("photoUrl")}
              />
              {watch("photoUrl") && (
                <div className="flex gap-3 items-center mt-2">
                  <img
                    src={watch("photoUrl")}
                    alt={t("onboarding.facility.photoUrl")}
                    className="object-cover w-16 h-16 rounded-lg border border-gray-200"
                    onError={e => {
                      (e.currentTarget as HTMLImageElement).style.display =
                        "none";
                    }}
                  />
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <SpecialtySearchAutocomplete
                specialties={specialtiesForWidget}
                selectedSpecialties={selectedSpecialties}
                onSpecialtiesChange={setSelectedSpecialties}
                title={t("admin.facilities.addModal.specialtiesTitle")}
                label={t("admin.facilities.addModal.specialtiesLabel")}
                placeholder={t(
                  "admin.facilities.addModal.specialtiesPlaceholder"
                )}
                getSelectedCountText={count =>
                  t("admin.facilities.addModal.specialtiesSelected", { count })
                }
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? t("common.processing")
              : t("admin.facilities.addModal.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
